/**
 * REST API adapters for Auth mutations
 * Transforms REST responses to match GraphQL structure
 *
 * Usage: Replace import in provider.tsx:
 * - import { useTokenAuthMutation } from './mutations';
 * + import { useTokenAuthMutation } from './mutations.rest';
 */
import type { LoginRequest, LoginResponse } from '@api';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';

import { authApi, extractApiErrors } from '@api';
import { AccountErrorCode } from '@gqlTypes/globalTypes';

import type {
  TokenAuth,
  TokenAuth_tokenCreate_errors,
  TokenAuth_tokenCreate_user,
  TokenAuthVariables,
} from './gqlTypes/TokenAuth';

/**
 * Transform REST user to GraphQL TokenAuth_tokenCreate_user format
 */
const transformUserToGraphQL = (
  user: LoginResponse['user'],
): TokenAuth_tokenCreate_user | null => {
  if (!user) return null;

  return {
    __typename: 'User',
    email: user.email,
    firstName: user.first_name,
    lastName: user.last_name,
    fullName: user.full_name,
    companyName: user.company_name,
    billingAddress: user.billing_address
      ? {
          __typename: 'BillingAddress',
          email: user.billing_address.email,
          address: user.billing_address.address,
          city: user.billing_address.city,
          companyName: user.billing_address.company_name || null,
          firstName: user.billing_address.first_name || null,
          isCompany: user.billing_address.is_company,
          lastName: user.billing_address.last_name || null,
          phone: user.billing_address.phone || null,
          postalCode: user.billing_address.postal_code,
          taxId: user.billing_address.tax_id || null,
        }
      : null,
  };
};

/**
 * Transform REST API errors to GraphQL error format
 */
const transformErrorsToGraphQL = (error: AxiosError): TokenAuth_tokenCreate_errors[] => {
  const apiErrors = extractApiErrors(error);
  return apiErrors.map((e) => ({
    __typename: 'AccountError' as const,
    /** Zachowaj kod z API (np. login_stepup_required) — GraphQL enum może go nie zawierać. */
    code: (e.code ? (e.code as AccountErrorCode) : AccountErrorCode.INVALID),
    message: e.message || 'Authentication failed',
    field: e.field || null,
  }));
};

/**
 * REST adapter for useTokenAuthMutation
 * Matches Apollo useMutation interface
 */
export const useTokenAuthMutation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const mutate = useCallback(async (options: { variables: TokenAuthVariables }) => {
    setLoading(true);
    setError(undefined);

    try {
      const loginData: LoginRequest = {
        email: options.variables.email,
        password: options.variables.password,
        turnstile_token: (options.variables as any).turnstileToken || undefined,
        turnstile_action: (options.variables as any).turnstileAction || undefined,
      };

      const response = await authApi.login(loginData);

      const result: TokenAuth = {
        tokenCreate: {
          __typename: 'CreateToken',
          errors: [],
          user: transformUserToGraphQL(response.user),
          token: response.access,
        },
      };

      setLoading(false);
      return { data: result };
    } catch (err) {
      setLoading(false);

      // Transform REST error to GraphQL format
      const result: TokenAuth = {
        tokenCreate: {
          __typename: 'CreateToken',
          errors: transformErrorsToGraphQL(err as AxiosError),
          user: null,
          token: null,
        },
      };

      // Don't throw - return errors in GraphQL format
      return { data: result };
    }
  }, []);

  return [mutate, { loading, error }] as const;
};
